// Period presets and CSV export shared by the accounting and finance screens
// (prototype `pages/finance/Ledgers.tsx → usePeriod`, `csv`).
import { csvCell, todayIso } from "@keel/core";
import { useSearchParams } from "react-router-dom";

/** Period from the URL (`?period=ytd` or `?from=&to=`), with a picker for the toolbar. */
export function usePeriod(defaultKey = "ytd") {
  const [params, setParams] = useSearchParams();
  const t = todayIso();
  const y = t.slice(0, 4);
  const m = t.slice(0, 7);
  const pm = new Date(`${m}-01T00:00:00Z`);
  pm.setUTCMonth(pm.getUTCMonth() - 1);
  const pmEnd = new Date(`${m}-01T00:00:00Z`);
  pmEnd.setUTCDate(0);
  const q = Math.floor((Number(t.slice(5, 7)) - 1) / 3) * 3 + 1;
  const presets: Record<string, [string, string, string]> = {
    mtd: ["This month", `${m}-01`, t],
    last: ["Last month", pm.toISOString().slice(0, 10), pmEnd.toISOString().slice(0, 10)],
    qtd: ["This quarter", `${y}-${String(q).padStart(2, "0")}-01`, t],
    ytd: ["Year to date", `${y}-01-01`, t],
  };
  const key = presets[params.get("period") ?? ""]
    ? params.get("period")!
    : params.get("from")
      ? "custom"
      : defaultKey;
  const from = key === "custom" ? params.get("from")! : presets[key]![1];
  const to = key === "custom" ? (params.get("to") ?? t) : presets[key]![2];
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const picker = (
    <div className="row" style={{ flexWrap: "wrap" }}>
      <div className="chips">
        {Object.entries(presets).map(([k, [l]]) => (
          <button
            type="button"
            key={k}
            className={`chip ${k === key ? "active" : ""}`}
            onClick={() => set({ period: k, from: null, to: null })}
          >
            {l}
          </button>
        ))}
      </div>
      <input
        type="date"
        className="select-sm"
        style={{ width: 140 }}
        value={from}
        onChange={(e) => set({ period: null, from: e.target.value, to })}
      />
      <span className="muted">–</span>
      <input
        type="date"
        className="select-sm"
        style={{ width: 140 }}
        value={to}
        onChange={(e) => set({ period: null, from, to: e.target.value })}
      />
    </div>
  );
  return { from, to, picker, params, set };
}

/** Download rows as a CSV file. */
export function downloadCsv(name: string, rows: (string | number | null | undefined)[][]) {
  const body = rows.map((r) => r.map(csvCell).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
  a.download = name;
  a.click();
}
